import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { LUNAR } from '../constants/testIds';
import { Ship, CheckCircle, Loader2, UploadCloud, X, Award, Rocket, Globe, BookOpen, UserCheck, Send } from 'lucide-react';
import { toast } from 'sonner';
import { Footer } from '../components/Footer';
import { Seo } from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Consign() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [acquisition, setAcquisition] = useState('');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const uploadPhotos = async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const form = new FormData();
        form.append('file', file);
        const res = await axios.post(`${API}/consignments/upload-photo`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push(`${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
      }
      setPhotos((prev) => [...prev, ...uploaded]);
      toast.success('Photos Added', { description: `${uploaded.length} photo(s) attached to your inquiry.` });
    } catch (err) {
      const msg = err.response?.data?.detail || 'Unable to upload one or more photos.';
      toast.error('Upload Failed', { description: msg });
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (url) => setPhotos((prev) => prev.filter((p) => p !== url));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !itemDescription.trim()) {
      toast.error('A Few Details Needed', { description: 'Please provide your name, email, and a description of your item.' });
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/consignments`, {
        name,
        email,
        phone,
        item_description: itemDescription,
        acquisition,
        additional_notes: additionalNotes,
        photos,
      });
      setSuccess(true);
      toast.success('Inquiry Received', { description: 'Thank you — a Lunar Legacies specialist will be in touch shortly.' });
    } catch (err) {
      toast.error('Submission Failed', { description: 'Something went wrong. Please try again in a moment.' });
    } finally {
      setSubmitting(false);
    }
  };

  const pillars = [
    { icon: Award, title: '25+ Years of Experience', text: 'Founded in 1998 by Don Willis, with dedicated space memorabilia auctions held since 2009.' },
    { icon: Rocket, title: 'Apollo & Gemini Specialists', text: 'We specialize in Mercury, Gemini, and Apollo-era hardware, flown artifacts, autographs, and mission documentation.' },
    { icon: Globe, title: 'A Broad Collector Network', text: 'A network of collectors, institutions, and estates helps your piece find the right home.' },
    { icon: BookOpen, title: 'We Handle the Evaluation', text: 'You don\'t need to know what you have. Our experts research, authenticate, and catalog everything for you.' },
    { icon: UserCheck, title: 'Personalized Service', text: 'A dedicated specialist guides you through the entire process with warmth, patience, and complete discretion.' },
    { icon: Ship, title: 'Secure, Insured Handling', text: 'Fully insured custody and careful logistics protect your item every step of the way.' },
  ];

  return (
    <div className="min-h-screen bg-luxury-black text-white py-12 px-6 md:px-12 lg:px-24">
      <Seo
        path="/consign"
        title="Consign Space Memorabilia | Sell With Lunar Legacies"
        description="Consign authentic space memorabilia with Lunar Legacies. We seek astronaut artifacts, flown items, mission hardware, documents, autographs and photographs. Trusted, insured handling since 1998."
      />
      <div className="max-w-3xl mx-auto">
        {/* Title */}
        <div className="text-center mb-10">
          <span className="text-xs uppercase tracking-[0.3em] text-luxury-gold font-semibold flex items-center justify-center gap-1.5 mb-3">
            <Ship size={14} className="stroke-[1.5]" /> Sell Through Auction
          </span>
          <h1 className="text-3xl md:text-5xl font-serif-luxury text-white">Consign Space Memorabilia</h1>
          <p className="text-sm text-luxury-muted leading-relaxed mt-4 max-w-xl mx-auto font-sans-luxury">
            Founded in 1998, Lunar Legacies has specialized in authentic space memorabilia, holding dedicated auctions since 2009. We are looking for astronaut artifacts, flown items, mission hardware, documents, photographs, autographs, and related space collectibles. Tell us what you have and we{"'"}ll be in touch.
          </p>
        </div>

        {/* Form Card */}
        <div className="border border-white/10 bg-luxury-charcoal p-8 md:p-12">
          {success ? (
            <div className="text-center py-10 space-y-5" data-testid="consign-success">
              <CheckCircle size={52} className="text-luxury-gold mx-auto" />
              <h2 className="text-2xl md:text-3xl font-serif-luxury text-white">Thank You — Inquiry Received</h2>
              <p className="text-sm text-luxury-muted font-sans-luxury max-w-lg mx-auto leading-relaxed">
                Lunar Legacies will review your consignment inquiry and follow up with you about selling through auction.
              </p>
              <div className="pt-3 flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/')}
                  className="btn-gold font-sans-luxury text-[10px] uppercase tracking-[0.2em] font-semibold px-8 py-3.5 rounded-none"
                  data-testid="consign-back-home"
                >
                  Return Home
                </button>
                <button
                  onClick={() => {
                    setSuccess(false);
                    setItemDescription('');
                    setAcquisition('');
                    setAdditionalNotes('');
                    setPhotos([]);
                  }}
                  className="border border-white/20 hover:border-white text-white font-sans-luxury text-[10px] uppercase tracking-[0.2em] px-8 py-3.5 transition-colors duration-300"
                >
                  Submit Another Item
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-2">Name</label>
                  <input
                    type="text" required value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="w-full bg-black border border-white/10 text-white placeholder-white/25 focus:border-luxury-gold text-xs px-4 py-3.5 outline-none rounded-none transition-colors duration-300"
                    data-testid="consign-name"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-2">Email <span className="text-luxury-gold">*</span></label>
                  <input
                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="w-full bg-black border border-white/10 text-white placeholder-white/25 focus:border-luxury-gold text-xs px-4 py-3.5 outline-none rounded-none transition-colors duration-300"
                    data-testid="consign-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-2">Phone <span className="text-luxury-muted normal-case tracking-normal">(optional)</span></label>
                <input
                  type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                  placeholder="Best number to reach you"
                  className="w-full bg-black border border-white/10 text-white placeholder-white/25 focus:border-luxury-gold text-xs px-4 py-3.5 outline-none rounded-none transition-colors duration-300"
                  data-testid="consign-phone"
                />
              </div>

              <div>
                <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-2">Description of Item(s) <span className="text-luxury-gold">*</span></label>
                <textarea
                  rows={5} required value={itemDescription} onChange={(e) => setItemDescription(e.target.value)}
                  placeholder="Describe the item(s) as best you can — what it is (or what you think it is), any markings, mission, names, or history you know. A rough description is fine."
                  className="w-full bg-black border border-white/10 text-white placeholder-white/25 focus:border-luxury-gold text-xs px-4 py-3.5 outline-none rounded-none resize-none transition-colors duration-300"
                  data-testid="consign-item"
                ></textarea>
              </div>

              {/* Photo upload */}
              <div>
                <label className="block text-[10px] uppercase tracking-widest text-luxury-gold font-sans-luxury mb-2">Upload Photos <span className="text-luxury-muted normal-case tracking-normal">(optional)</span></label>
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); uploadPhotos(e.dataTransfer.files); }}
                  className="border border-dashed border-white/20 hover:border-luxury-gold/50 bg-black/30 p-8 text-center transition-colors duration-300 flex flex-col items-center gap-3"
                  data-testid="consign-upload-zone"
                >
                  {uploading ? (
                    <>
                      <Loader2 size={26} className="text-luxury-gold animate-spin" />
                      <span className="text-[10px] font-serif-luxury uppercase tracking-widest text-luxury-gold">Uploading Photos…</span>
                    </>
                  ) : (
                    <>
                      <UploadCloud size={30} className="text-luxury-muted stroke-[1.2]" />
                      <p className="text-xs text-luxury-muted font-sans-luxury">Drag &amp; drop photos here, or choose from your device. Snapshots are perfectly fine.</p>
                      <p className="text-[11px] text-luxury-goldLight font-sans-luxury">Photos help us evaluate your items faster.</p>
                    </>
                  )}
                  <label className="cursor-pointer bg-white/5 border border-white/10 hover:border-luxury-gold text-white hover:text-luxury-gold px-5 py-2.5 text-[9px] uppercase tracking-widest font-sans-luxury transition-colors duration-300">
                    Choose Photos
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadPhotos(e.target.files)} data-testid="consign-upload-input" />
                  </label>
                </div>

                {photos.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4" data-testid="consign-photo-grid">
                    {photos.map((url, idx) => (
                      <div key={idx} className="relative aspect-square border border-white/10 overflow-hidden bg-black group">
                        <img src={url} alt={`Attachment ${idx + 1}`} onError={(e) => { e.currentTarget.style.opacity = '0.2'; }} className="w-full h-full object-cover" />
                        <button type="button" onClick={() => removePhoto(url)} className="absolute top-1 right-1 p-1 bg-black/70 text-white hover:text-red-400 border border-white/10" title="Remove">
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting || uploading || !name.trim() || !email.trim() || !itemDescription.trim()}
                className="btn-gold w-full font-sans-luxury uppercase tracking-[0.2em] text-xs font-semibold py-4 rounded-none flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="consign-submit"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                {submitting ? 'Submitting…' : 'Submit Consignment Inquiry'}
              </button>
              <p className="text-[10px] text-luxury-muted/70 font-sans-luxury text-center leading-relaxed">
                No obligation. Lunar Legacies will review your inquiry and follow up about selling through auction.
              </p>
            </form>
          )}
        </div>

        {/* Why Consign With Lunar Legacies — Trust Section */}
        <section className="mt-20 md:mt-28 border-t border-white/5 pt-16 md:pt-24" data-testid="why-consign-section">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs uppercase tracking-[0.3em] text-luxury-gold font-semibold">A Legacy of Trust</span>
            <h2 className="text-3xl md:text-5xl font-serif-luxury text-white mt-3">Why Consign With Lunar Legacies</h2>
            <p className="text-sm text-luxury-muted leading-relaxed mt-4 font-sans-luxury">
              For over 25 years, estates, collectors, and families have trusted Lunar Legacies to research, authenticate, and place their space memorabilia — handled with expert evaluation, care, and discretion.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {pillars.map((pillar, idx) => {
              const Icon = pillar.icon;
              return (
                <div key={idx} className="group bg-luxury-charcoal p-8 md:p-10 hover:bg-luxury-black transition-colors duration-500">
                  <div className="w-12 h-12 border border-luxury-gold/30 flex items-center justify-center text-luxury-gold mb-6 group-hover:border-luxury-gold transition-colors duration-500">
                    <Icon size={22} className="stroke-[1.3]" />
                  </div>
                  <h3 className="text-lg font-serif-luxury text-white mb-3 group-hover:text-luxury-goldLight transition-colors duration-300">{pillar.title}</h3>
                  <p className="text-xs text-luxury-muted leading-relaxed font-sans-luxury">{pillar.text}</p>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-14">
            <p className="text-sm text-luxury-muted font-serif-luxury italic">
              Serving collectors, estates, and families with authentic space memorabilia since 1998.
            </p>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}
